import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ChevronLeft, ChevronRight, GripVertical, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import * as adminApi from "@/api/admin";
import type { Partner } from "@/api/admin";

function LogoDropzone({ file, onChange }: { file: File | null; onChange: (f: File | null) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (f && f.type.startsWith('image/')) onChange(f);
  };

  if (file && preview) {
    return (
      <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="w-16 h-16 rounded-md bg-white border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
          <img src={preview} alt="preview" className="w-full h-full object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
          <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
        </div>
        <button
          type="button"
          onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
          className="text-gray-400 hover:text-red-500 p-1"
          aria-label="Remove image"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <label
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
        dragging
          ? 'border-[#00d9d9] bg-[#00d9d9]/5'
          : 'border-gray-300 hover:border-[#00d9d9] hover:bg-gray-50'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />
      <div className="w-10 h-10 rounded-full bg-[#00d9d9]/10 flex items-center justify-center text-[#00bfc0]">
        <Upload size={18} />
      </div>
      <p className="text-sm text-gray-600">
        <span className="font-medium text-[#00bfc0]">Click to upload</span> or drag and drop
      </p>
      <p className="text-xs text-gray-400">PNG, JPG, SVG or WEBP</p>
    </label>
  );
}

export function PartnersView() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Reordering: `partners` holds the current (possibly unsaved) order,
  // `savedOrder` the ids as they came from the server.
  const [savedOrder, setSavedOrder] = useState<number[]>([]);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const orderChanged =
    partners.length === savedOrder.length && partners.some((p, i) => p.id !== savedOrder[i]);

  const load = () => {
    setLoading(true);
    adminApi.getPartners()
      .then(list => { setPartners(list); setSavedOrder(list.map(p => p.id)); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !logoFile) return;
    setAdding(true);
    await adminApi.createPartner({
      name,
      logo: logoFile,
      website_url: websiteUrl,
      order: partners.length,
    });
    setName('');
    setLogoFile(null);
    setWebsiteUrl('');
    setAdding(false);
    load();
  };

  const moveTo = (id: number, toIndex: number) => {
    setPartners(prev => {
      const from = prev.findIndex(p => p.id === id);
      if (from === -1 || toIndex < 0 || toIndex >= prev.length || from === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(toIndex, 0, moved!);
      return next;
    });
  };

  const discardOrder = () => {
    setPartners(prev => savedOrder.map(id => prev.find(p => p.id === id)!).filter(Boolean));
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const list = await adminApi.reorderPartners(partners.map(p => p.id));
      setPartners(list);
      setSavedOrder(list.map(p => p.id));
      toast.success("Partner order saved");
    } catch {
      toast.error("Could not save the order", { description: "Reload the page and try again." });
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    await adminApi.deletePartner(id);
    setDeletingId(null);
    setConfirmDeleteId(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Add Partner</h2>
        <form onSubmit={handleAdd} className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] space-y-1">
              <label className="text-xs text-gray-500">Name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Partner name" required />
            </div>
            <div className="flex-1 min-w-[200px] space-y-1">
              <label className="text-xs text-gray-500">Website URL</label>
              <Input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://..." type="url" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Logo image</label>
            <LogoDropzone file={logoFile} onChange={setLogoFile} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={adding || !logoFile} className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Add Partner</>}
            </Button>
          </div>
        </form>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load partners.</AlertDescription>
        </Alert>
      )}
      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
      ) : partners.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">No partners yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 min-h-9">
            <p className="text-xs text-gray-400">
              Drag partners to change the order they appear on the site.
            </p>
            {orderChanged && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600">Unsaved order</span>
                <button
                  type="button"
                  onClick={discardOrder}
                  disabled={savingOrder}
                  className="px-3 h-8 rounded-md border border-gray-200 bg-white text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Discard
                </button>
                <Button
                  type="button"
                  size="sm"
                  onClick={saveOrder}
                  disabled={savingOrder}
                  className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white h-8"
                >
                  {savingOrder ? <Loader2 size={14} className="animate-spin" /> : 'Save order'}
                </Button>
              </div>
            )}
          </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {partners.map((p, index) => (
            <div
              key={p.id}
              draggable={confirmDeleteId !== p.id}
              onDragStart={e => {
                setDraggingId(p.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(p.id));
              }}
              onDragOver={e => {
                if (draggingId === null) return;
                e.preventDefault();
                if (draggingId !== p.id) moveTo(draggingId, index);
              }}
              onDrop={e => e.preventDefault()}
              onDragEnd={() => setDraggingId(null)}
              className={`border rounded-lg p-3 pt-7 flex flex-col items-center gap-2 group relative bg-white overflow-hidden cursor-grab active:cursor-grabbing transition-all ${
                draggingId === p.id
                  ? 'border-[#00d9d9] border-dashed opacity-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="absolute top-2 left-2 flex items-center gap-1 text-gray-300 group-hover:text-gray-400">
                <GripVertical size={14} />
                <span className="text-[10px] font-semibold tabular-nums">{index + 1}</span>
              </div>
              <img src={p.logo_url} alt={p.name} draggable={false} className="w-full h-20 object-contain pointer-events-none" />
              <span className="text-xs text-gray-600 font-medium text-center">{p.name}</span>
              {p.website_url && (
                <a
                  href={p.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-[#00bfc0] hover:underline truncate max-w-full"
                  title={p.website_url}
                >
                  {p.website_url.replace(/^https?:\/\//, '')}
                </a>
              )}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => moveTo(p.id, index - 1)}
                  disabled={index === 0}
                  className="p-1 rounded text-gray-400 hover:text-[#00bfc0] hover:bg-gray-50 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:bg-transparent"
                  aria-label={`Move ${p.name} earlier`}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => moveTo(p.id, index + 1)}
                  disabled={index === partners.length - 1}
                  className="p-1 rounded text-gray-400 hover:text-[#00bfc0] hover:bg-gray-50 disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:bg-transparent"
                  aria-label={`Move ${p.name} later`}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
              <button
                onClick={() => setConfirmDeleteId(p.id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 size={14} />
              </button>
              {confirmDeleteId === p.id && (
                <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-3 text-center">
                  <p className="text-xs text-gray-700">
                    Delete <span className="font-semibold">{p.name}</span>?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={deletingId === p.id}
                      className="px-3 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      disabled={deletingId === p.id}
                      className="px-3 py-1 text-xs rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {deletingId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}
